import {useState, useEffect, useCallback} from 'react';
import {TemplateStructure, TemplateItem, TextTemplateItem} from '../types/templateStructure';
import {convertVariableSpansToText} from '../utils/variableUtils';
import {convertHtmlToWhatsAppFormat} from '../utils/formatUtils';

interface UseTemplateStructureProps {
    initialStructure: TemplateStructure;
    dependencies: any[];
    onChange?: (structure: TemplateStructure) => void;
}

// Helper function to serialize structure to text for sending.
// Inline items substitute variable-converted param values into the template text.
// Text items have variable spans converted back to [variable_name] placeholders.
export const serializeStructure = (struct: TemplateStructure): string => {
    const parts: string[] = [];
    struct?.forEach(item => {
        if(item.type === 'inline') {
            if(item.params) {
                item.params.forEach(param => {
                    const paramValue = param.value ? convertVariableSpansToText(param.value) : '';
                    parts.push(paramValue);
                });
            }
        } else if(item.type === 'text') {
            parts.push(convertVariableSpansToText(item.value));
        } else if(item.type === 'empty') {
            parts.push('');
        }
    });
    return parts.join('\n');
};

const convertToWhatsappText = (htmlContent: string) => {
    const whatsappFormatted = convertHtmlToWhatsAppFormat(htmlContent);
    return convertVariableSpansToText(whatsappFormatted);
};

export function useTemplateStructure({initialStructure, dependencies, onChange}: UseTemplateStructureProps) {
    const [structure, setStructure] = useState<TemplateStructure>(initialStructure);
    const [hasChanged, setHasChanged] = useState(false);

    // Reset structure when dependencies change
    useEffect(() => {
        if(initialStructure) {
            setStructure(initialStructure);
            setHasChanged(false);
        }
    }, dependencies);

    const handleItemChange = useCallback((index: number, updatedItem: TemplateItem) => {
        setStructure(prevStructure => {
            const newStructure = [...prevStructure];
            newStructure[index] = updatedItem;
            
            if(onChange) {
                onChange(newStructure);
            }
            return newStructure;
        });
        setHasChanged(true);
    }, [onChange]);

    const prepareForSave = useCallback((): TextTemplateItem[] => {
        const values: TextTemplateItem[] = [];
        let currentIndex = 1;
        
        structure.forEach(item => {
            if(item.type === 'inline') {
                // Add each param as a separate TextTemplateItem (only if params exist)
                if(item.params) {
                    item.params.forEach(param => {
                        const textValue = convertToWhatsappText(param.value);
                        values.push({
                            index: currentIndex++,
                            type: 'text',
                            value: textValue
                        });
                    });
                }
            } else if(item.type === 'text') {
                const textValue = convertToWhatsappText(item.value);
                values.push({
                    index: currentIndex++,
                    type: 'text',
                    value: textValue
                });
            }
        });
        
        return values;
    }, [structure]);

    const clearEditing = useCallback(() => {
        setHasChanged(false);
    }, []);
    
    return {
        structure,
        hasChanged,
        handleItemChange,
        prepareForSave,
        clearEditing
    };
}
